import {
    Checkbox,
    Divider,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import {
    SettingsInlineEdit,
    SettingsOptionRow,
} from '../../../../../components/settings/primitives';
import { InlineCellDisplay } from '../../../../../components/settings/UsersInlineCells';
import { SupplierSearchField } from '../../../../../components/SupplierSearchField';
import {
    formatLkqPricingStatesLabel,
    LKQ_PRICING_ALL_LABEL,
    LKQ_PRICING_WILDCARD,
    sortLkqPricingStates,
} from '../../../../../utils/lkqPricingConstants';

export interface PricingVendor {
    supplierId: string;
    supplierName: string;
}

export interface InlinePricingVendorCellProps {
    value: PricingVendor;
    onSave: (next: PricingVendor) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

/**
 * The vendor one pricing row holds — a markup, a discount, or a discount flag
 * expectation — changed in place: type three
 * characters and pick from the suppliers PartBay knows, or hold every vendor to
 * the discount. Saved on select, the same way the other single-value cell
 * editors in Settings work — a vendor cannot be free-typed, because the
 * row is keyed by supplier id.
 */
export const InlinePricingVendorCell = ({
    value,
    onSave,
    isDisabled = false,
    ariaLabel,
}: InlinePricingVendorCellProps) => {
    const isAll = value.supplierId === LKQ_PRICING_WILDCARD;

    return (
        <SettingsInlineEdit
            display={<InlineCellDisplay label={value.supplierName} hasChevron={!isDisabled} />}
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW="280px"
        >
            {(close) => (
                <VStack align="stretch" spacing={2}>
                    <SettingsOptionRow
                        label={`${LKQ_PRICING_ALL_LABEL} vendors`}
                        isEmphasized
                        isSelected={isAll}
                        onToggle={() => {
                            if (!isAll) {
                                onSave({
                                    supplierId: LKQ_PRICING_WILDCARD,
                                    supplierName: LKQ_PRICING_ALL_LABEL,
                                });
                            }
                            close();
                        }}
                    />

                    <SupplierSearchField
                        autoFocus
                        placeholder="Search vendors"
                        emptyMessage="No vendors found"
                        excludeIds={isAll ? undefined : [value.supplierId]}
                        onSelect={(supplier) => {
                            onSave({
                                supplierId: supplier._id,
                                supplierName: supplier.title,
                            });
                            close();
                        }}
                    />
                </VStack>
            )}
        </SettingsInlineEdit>
    );
};

export interface InlinePricingStatesCellProps {
    /** Stored states: `['*']` for every state. */
    value: string[];
    /** States the org operates in, from its locations. */
    options: string[];
    onSave: (next: string[]) => void;
    isDisabled?: boolean;
    ariaLabel: string;
}

const isSameStates = (a: string[], b: string[]) =>
    a.length === b.length && a.every((state) => b.includes(state));

/**
 * The states one pricing row applies in. "All States" is stored as the
 * wildcard rather than as the list, so a row keeps covering a state the shop
 * opens in later; picking states narrows it and replaces the wildcard.
 *
 * Built like the locations picker — an All row above a checkbox list — rather
 * than as tinted option rows: states are ticked in sets, and a panel where
 * every row is filled in is unreadable at this length. Saves on close, so a
 * handful of states is one edit rather than one per state.
 */
export const InlinePricingStatesCell = ({
    value,
    options,
    onSave,
    isDisabled = false,
    ariaLabel,
}: InlinePricingStatesCellProps) => {
    const [draft, setDraft] = useState<string[]>(value);
    const sortedOptions = useMemo(
        () => [...options].sort((a, b) => a.localeCompare(b)),
        [options],
    );
    const isAll = draft.includes(LKQ_PRICING_WILDCARD);
    const selected = new Set(isAll ? [] : draft);

    const toggle = (state: string, checked: boolean) => {
        // Leaving "All States" starts from every state, so unchecking one reads
        // the way it looks rather than wiping the row's coverage.
        const base = isAll ? sortedOptions : Array.from(selected);
        setDraft(
            checked
                ? Array.from(new Set([...base, state]))
                : base.filter((current) => current !== state),
        );
    };

    return (
        <SettingsInlineEdit
            display={
                <InlineCellDisplay
                    label={formatLkqPricingStatesLabel(value)}
                    isMuted={value.length === 0}
                    hasChevron={!isDisabled}
                />
            }
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            minW="240px"
            onOpen={() => setDraft(value)}
            onClose={() => {
                // Nothing selected is not a row, so an emptied list falls back
                // to what was saved rather than deleting the row's coverage.
                if (draft.length === 0) return;
                const next = isAll ? [LKQ_PRICING_WILDCARD] : sortLkqPricingStates(draft);
                if (!isSameStates(next, value)) onSave(next);
            }}
        >
            <VStack align="stretch" spacing={2}>
                <Checkbox
                    size="sm"
                    colorScheme="partbay-blue"
                    isChecked={isAll}
                    isIndeterminate={!isAll && selected.size > 0}
                    onChange={(e) => setDraft(e.target.checked ? [LKQ_PRICING_WILDCARD] : [])}
                >
                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                        {LKQ_PRICING_ALL_LABEL} States
                    </Text>
                </Checkbox>
                <Divider />
                {sortedOptions.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" px={1} py={1}>
                        No states on your locations yet.
                    </Text>
                ) : (
                    <VStack align="stretch" spacing={1} maxH="220px" overflowY="auto">
                        {sortedOptions.map((state) => (
                            <Checkbox
                                key={state}
                                size="sm"
                                colorScheme="partbay-blue"
                                isChecked={isAll || selected.has(state)}
                                onChange={(e) => toggle(state, e.target.checked)}
                            >
                                <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                    {state}
                                </Text>
                            </Checkbox>
                        ))}
                    </VStack>
                )}
            </VStack>
        </SettingsInlineEdit>
    );
};
